import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Copy,
  RefreshCw,
  Mail,
  ChevronDown,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const CATEGORIES = [
  { value: "introduction", label: "Introduction", color: "bg-blue-100 text-blue-700" },
  { value: "follow_up", label: "Follow-up", color: "bg-yellow-100 text-yellow-700" },
  { value: "response", label: "Response", color: "bg-green-100 text-green-700" },
  { value: "meeting", label: "Meeting Request", color: "bg-purple-100 text-purple-700" },
  { value: "proposal", label: "Proposal", color: "bg-orange-100 text-orange-700" }
];

const MERGE_VARIABLES = [
  { key: "{{factory_name}}", desc: "Factory/Company name" },
  { key: "{{contact_name}}", desc: "Contact's full name" },
  { key: "{{contact_title}}", desc: "Contact's job title" },
  { key: "{{country}}", desc: "Factory country" },
  { key: "{{your_name}}", desc: "Your name" }
];

const ExportTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    name: "",
    category: "introduction",
    subject: "",
    body: "",
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/export/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await axios.post(`${API}/export/templates`, formData);
      toast.success("Template created");
      setShowDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to create template");
    }
  };

  const handleUpdate = async () => {
    if (!selectedTemplate) return;
    try {
      await axios.patch(`${API}/export/templates/${selectedTemplate.id}`, formData);
      toast.success("Template updated");
      setShowDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to update template");
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await axios.delete(`${API}/export/templates/${selectedTemplate.id}`);
      toast.success("Template deleted");
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post(`${API}/export/templates`, {
        name: `${template.name} (Copy)`,
        category: template.category,
        subject: template.subject,
        body: template.body,
        is_active: true
      });
      toast.success("Template duplicated");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      subject: template.subject,
      body: template.body,
      is_active: template.is_active
    });
    setIsEditing(true);
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsEditing(false);
    setShowDialog(true);
  };

  const openPreview = (template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category: "introduction",
      subject: "",
      body: "",
      is_active: true
    });
    setSelectedTemplate(null);
    setIsEditing(false);
  };

  const insertVariable = (variable) => {
    setFormData({
      ...formData,
      body: formData.body + variable
    });
  };

  const filteredTemplates = categoryFilter === "all" 
    ? templates 
    : templates.filter(t => t.category === categoryFilter);

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || { label: category, color: "bg-slate-100 text-slate-700" };
  };

  return (
    <div className="space-y-6" data-testid="export-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email Templates</h1>
          <p className="text-slate-500">Manage your outreach email templates</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchTemplates} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4" />
            New Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{filteredTemplates.length} templates</span>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No templates yet</h3>
            <p className="text-slate-500 mb-4">Create your first email template to start outreach</p>
            <Button onClick={openCreateDialog} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => {
            const catInfo = getCategoryInfo(template.category);
            return (
              <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base truncate">{template.name}</CardTitle>
                      <Badge variant="outline" className={`mt-1 ${catInfo.color}`}>
                        {catInfo.label}
                      </Badge>
                    </div>
                    {!template.is_active && (
                      <Badge variant="secondary" className="bg-red-100 text-red-700">Inactive</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Subject</p>
                      <p className="text-sm font-medium text-slate-800 truncate">{template.subject}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Preview</p>
                      <p className="text-sm text-slate-600 line-clamp-2">{template.body.substring(0, 100)}...</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                    <Button variant="ghost" size="sm" onClick={() => openPreview(template)} className="gap-1">
                      <Eye className="w-3 h-3" />
                      Preview
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(template)} className="gap-1">
                      <Edit2 className="w-3 h-3" />
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(template)} className="gap-1">
                      <Copy className="w-3 h-3" />
                      Copy
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setSelectedTemplate(template); setShowDeleteDialog(true); }}
                      className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Template" : "Create Template"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Initial Introduction"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Subject Line *</Label>
              <Input 
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Partnership opportunity with {{factory_name}}"
                data-testid="template-subject-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Email Body *</Label>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">Insert:</span>
                  {MERGE_VARIABLES.map(v => (
                    <Button 
                      key={v.key}
                      variant="outline" 
                      size="sm" 
                      className="h-6 px-2 text-xs"
                      onClick={() => insertVariable(v.key)}
                      title={v.desc}
                    >
                      {v.key.replace(/[{}]/g, '')}
                    </Button>
                  ))}
                </div>
              </div>
              <Textarea 
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={10}
                placeholder="Write your email content here. Use merge variables like {{contact_name}} to personalize..."
                className="font-mono text-sm"
                data-testid="template-body-textarea"
              />
            </div>

            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs font-medium text-slate-600 mb-2">Available Merge Variables:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {MERGE_VARIABLES.map(v => (
                  <div key={v.key} className="flex items-center gap-2">
                    <code className="bg-white px-1.5 py-0.5 rounded border text-blue-600">{v.key}</code>
                    <span className="text-slate-500">{v.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button 
              onClick={isEditing ? handleUpdate : handleCreate}
              disabled={!formData.name || !formData.subject || !formData.body}
              data-testid="template-save-btn"
            >
              {isEditing ? "Update" : "Create"} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                  <Mail className="w-4 h-4" />
                  <span>Email Preview</span>
                </div>
                <div className="bg-white rounded border p-4">
                  <div className="border-b pb-3 mb-3">
                    <p className="text-xs text-slate-500">Subject:</p>
                    <p className="font-medium">{selectedTemplate.subject}</p>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-slate-700">
                    {selectedTemplate.body}
                  </div>
                </div>
              </div>
              
              <div className="text-xs text-slate-500 text-center">
                Variables like {"{{contact_name}}"} will be replaced with actual values when sending
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
            <Button onClick={() => { setShowPreview(false); openEditDialog(selectedTemplate); }}>
              Edit Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExportTemplates;
